import { fetchData, updateData, postData } from './api';
import { selectCurrentGroupId } from './groups_slice';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../store';

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface SupportResourcesState {
  data: unknown;
  status: AsyncStatus;
  updateStatus: AsyncStatus;
  createStatus: AsyncStatus;
  error: string | null;
  updateError: string | null;
  createError: string | null;
}

const initialState: SupportResourcesState = {
  data: null,
  status: 'idle',
  updateStatus: 'idle',
  createStatus: 'idle',
  error: null,
  updateError: null,
  createError: null,
};

export const fetchSupportResources = createAsyncThunk<unknown, void, { state: RootState }>(
  'supportResources/fetch',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const isSeAdmin = state.authentication.user?.burnout_role === 'se_admin';
      const practice_group_id = isSeAdmin
        ? selectCurrentGroupId(state)
        : (state.authentication.user?.practice_group_ids as number[])[0];

      if (!practice_group_id) return rejectWithValue('No practice_group_id');

      const raw = await fetchData({ endpoint: 'support_resources', filters: { practice_group_id } });

      if (raw === undefined || raw === null) return [];
      if (Array.isArray(raw)) return raw;
      const r = raw as Record<string, unknown>;
      if (r.data !== undefined) {
        if (Array.isArray(r.data)) return r.data;
        const inner = r.data as Record<string, unknown>;
        if (inner.data !== undefined) return inner.data;
        return r.data;
      }
      if (r.payload !== undefined) return r.payload;
      return raw;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch support resources');
    }
  },
  {
    condition: (_, { getState }) => getState().support_resources.status !== 'loading',
  }
);

export const updateSupportResources = createAsyncThunk<unknown, any, { state: RootState }>(
  'supportResources/update',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const isSeAdmin = state.authentication.user?.burnout_role === 'se_admin';
      const practice_group_id = isSeAdmin
        ? selectCurrentGroupId(state)
        : (state.authentication.user?.practice_group_ids as number[])[0];

      if (!practice_group_id) return rejectWithValue('No practice_group_id');

      payload.practice_group_id = practice_group_id;
      return await updateData({ endpoint: `support_resources/${payload.resource_id}`, payload });
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to update support resources');
    }
  },
  {
    condition: (_, { getState }) => getState().support_resources.updateStatus !== 'loading',
  }
);

export const createSupportResources = createAsyncThunk<unknown, any, { state: RootState }>(
  'supportResources/create',
  async (payload = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const isSeAdmin = state.authentication.user?.burnout_role === 'se_admin';
      const practice_group_id = isSeAdmin
        ? selectCurrentGroupId(state)
        : (state.authentication.user?.practice_group_ids as number[])[0];

      if (!practice_group_id) return rejectWithValue('No practice_group_id');

      payload.practice_group_id = practice_group_id;
      return await postData({ endpoint: 'support_resources', payload });
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to create support resources');
    }
  },
  {
    condition: (_, { getState }) => getState().support_resources.createStatus !== 'loading',
  }
);

const supportResourcesSlice = createSlice({
  name: 'support_resources',
  initialState,
  reducers: {
    clearSupportResourcesError(state) {
      state.error = null;
      state.updateError = null;
      state.createError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSupportResources.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchSupportResources.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload;
      })
      .addCase(fetchSupportResources.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) ?? action.error.message ?? null;
      })
      .addCase(createSupportResources.pending, (state) => {
        state.createStatus = 'loading';
        state.createError = null;
      })
      .addCase(createSupportResources.fulfilled, (state, action) => {
        state.createStatus = 'succeeded';
        if (action.payload !== undefined && action.payload !== null) {
          state.data = action.payload;
        }
      })
      .addCase(createSupportResources.rejected, (state, action) => {
        state.createStatus = 'failed';
        state.createError = (action.payload as string) ?? action.error.message ?? null;
      })
      .addCase(updateSupportResources.pending, (state) => {
        state.updateStatus = 'loading';
        state.updateError = null;
      })
      .addCase(updateSupportResources.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        state.data = action.payload;
      })
      .addCase(updateSupportResources.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.updateError = (action.payload as string) ?? action.error.message ?? null;
      });
  },
});

export const { clearSupportResourcesError } = supportResourcesSlice.actions;
export default supportResourcesSlice.reducer;
